import { computed, ref } from 'vue';

/**
 * The browser's own "install the app" dialog. Chrome, Edge and Android announce once, early, that the page can be
 * installed (`beforeinstallprompt`) and show their dialog only on the user's click, so the event is kept until then.
 * The event is not cancelled: the browser keeps its own install icon and banner, and the button is one more way in.
 * Safari on iPhone and iPad has no such dialog: there the app is added by hand from the share menu.
 */
type InstallPromptEvent = Event & {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const deferred = ref<InstallPromptEvent | null>(null);
const installed = ref(false);

const isStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

/**
 * iPadOS reports itself as a Mac, told apart by the touch screen.
 */
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/**
 * Called by the app entry right away: pages load later, and the early event would be missed by then.
 */
export const captureInstallPrompt = () => {
    installed.value = isStandalone();

    window.addEventListener('beforeinstallprompt', (event) => {
        deferred.value = event as InstallPromptEvent;
    });

    window.addEventListener('appinstalled', () => {
        deferred.value = null;
        installed.value = true;
    });
};

export function useInstallApp() {
    return {
        /** The browser installs the app by its own dialog. */
        canPrompt: computed(() => deferred.value !== null),
        /** An iPhone or iPad not running the app yet: it is added by hand, so the steps are shown. */
        needsSteps: computed(() => !installed.value && deferred.value === null && isIos()),
        /** Show the browser's dialog; it may be shown once per event. */
        prompt: async () => {
            const event = deferred.value;

            if (!event) {
                return;
            }

            deferred.value = null;

            // The browser may have used the event for its own banner already; then its install icon is the way.
            try {
                await event.prompt();
                await event.userChoice;
            } catch {
                return;
            }
        },
    };
}
